import React, { useEffect, useState } from 'react';
import { Opportunity } from '../types/opportunity';
import { REQUIRED_FIELDS, FIELD_LABELS } from '../hooks/useOpportunityStatus';
import { XIcon, CheckCircleIcon } from 'lucide-react';
interface TakeActionModalProps {
  opportunity: Opportunity;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedOpportunity: Opportunity) => void;
}
export function TakeActionModal({
  opportunity,
  isOpen,
  onClose,
  onSave
}: TakeActionModalProps) {
  const [formData, setFormData] = useState<Partial<Opportunity>>({});
  // Reset form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({});
    }
  }, [isOpen, opportunity]);
  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  // Determine missing fields
  const requiredFields = REQUIRED_FIELDS[opportunity.stage];
  const missingFields = requiredFields.filter(field => {
    const value = opportunity[field];
    return value === undefined || value === null || value === false || value === '';
  });
  const handleInputChange = (field: keyof Opportunity, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...opportunity,
      ...formData
    });
    onClose();
  };
  const isSuccessState = missingFields.length === 0;
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-71">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-72"></div>

      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0" data-id="element-73">
        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg" data-id="element-74">
          {/* Header */}
          <div className="bg-white px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between" data-id="element-75">
            <h2 className="text-lg font-bold text-gray-900" id="modal-title" data-id="element-76">
              Take Action
            </h2>
            <button type="button" className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" onClick={onClose} data-id="element-77">
              <span className="sr-only" data-id="element-78">Close</span>
              <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-79" />
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-6 sm:px-6" data-id="element-80">
            {isSuccessState ? <div className="text-center py-8" data-id="element-81">
                <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500 mb-4" data-id="element-82" />
                <h3 className="text-lg font-medium text-gray-900" data-id="element-83">
                  All requirements met!
                </h3>
                <p className="mt-2 text-sm text-gray-500" data-id="element-84">
                  There are no missing fields for the current stage.
                </p>
              </div> : <form id="action-form" onSubmit={handleSubmit} className="space-y-8" data-id="element-85">
                {/* Missing Fields Section */}
                {missingFields.length > 0 && <div data-id="element-86">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-87">
                      Missing Information
                    </h3>
                    <div className="space-y-4" data-id="element-88">
                      {missingFields.map(field => {
                  const label = FIELD_LABELS[field] || field;
                  const currentValue = formData[field] !== undefined ? formData[field] : opportunity[field];
                  // Render appropriate input based on field name
                  if (['borrowerName', 'propertyAddress'].includes(field)) {
                    return <div key={field} data-id="element-89">
                              <label htmlFor={field} className="block text-sm font-medium leading-6 text-gray-900" data-id="element-90">
                                {label}
                              </label>
                              <div className="mt-2" data-id="element-91">
                                <input type="text" name={field} id={field} value={currentValue as string || ''} onChange={e => handleInputChange(field, e.target.value)} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3" placeholder={`Enter ${label.toLowerCase()}`} data-id="element-92" />
                              </div>
                            </div>;
                  }
                  if (['loanAmount', 'creditScore'].includes(field)) {
                    return <div key={field} data-id="element-93">
                              <label htmlFor={field} className="block text-sm font-medium leading-6 text-gray-900" data-id="element-94">
                                {label}
                              </label>
                              <div className="mt-2" data-id="element-95">
                                <input type="number" name={field} id={field} value={currentValue as number || ''} onChange={e => handleInputChange(field, e.target.value ? Number(e.target.value) : undefined)} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3" placeholder={`Enter ${label.toLowerCase()}`} min={field === 'creditScore' ? 300 : undefined} max={field === 'creditScore' ? 850 : undefined} data-id="element-96" />
                              </div>
                            </div>;
                  }
                  if (['employmentVerified', 'incomeDocuments', 'appraisalOrdered', 'titleSearch'].includes(field)) {
                    return <div key={field} className="relative flex items-start" data-id="element-97">
                              <div className="flex h-6 items-center" data-id="element-98">
                                <input id={field} name={field} type="checkbox" checked={currentValue as boolean || false} onChange={e => handleInputChange(field, e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600" data-id="element-99" />
                              </div>
                              <div className="ml-3 text-sm leading-6" data-id="element-100">
                                <label htmlFor={field} className="font-medium text-gray-900" data-id="element-101">
                                  {label}
                                </label>
                              </div>
                            </div>;
                  }
                  return null;
                })}
                    </div>
                  </div>}
              </form>}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 border-t border-gray-200" data-id="element-102">
            {!isSuccessState && <button type="submit" form="action-form" className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto" data-id="element-103">
                Save Changes
              </button>}
            <button type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" onClick={onClose} data-id="element-104">
              {isSuccessState ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}